import { useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import { readLocaleCatalog } from "../../i18n/localeCatalog";
import { splitLocaleFromPath } from "../../i18n/localeRouting";

/**
 * Scrolls the window to the top whenever the LOGICAL route changes — i.e. the
 * path with its `/:locale` prefix stripped (issue #221).
 *
 * Without this, SPA navigation preserves the previous route's scroll position,
 * which in practice means:
 *  - Clicking a product card on the home page (user scrolled to "Featured
 *    Products") navigates to the PDP with the browser still scrolled down,
 *    placing the PDP's Add to Cart button directly beneath the sticky header.
 *  - Subsequent clicks on the button can hit the sticky header's icons
 *    (search, cart, theme toggle) instead of the button itself.
 *
 * Keying on the locale-stripped path means a locale switch (`/sv-se/products` →
 * `/en-us/products`) keeps the user where they are — switching language must not
 * yank them to the top of the same page.
 *
 * Mount once inside <Router> but outside <Routes>.
 */
export default function ScrollToTop(): null {
  const { pathname } = useLocation();
  const { supported } = readLocaleCatalog();
  const logicalPath = splitLocaleFromPath(pathname, supported).rest;

  useLayoutEffect(() => {
    // useLayoutEffect runs synchronously before the browser paints, so the
    // new route never displays at the old scroll position — no visible jump.
    // "auto" (not "smooth") is intentional: consistent with a fresh page
    // load and unaffected by prefers-reduced-motion. Keyed on logicalPath, so a
    // locale-only change (same logical page) doesn't re-run this effect.
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [logicalPath]);

  return null;
}
